import { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { supabase } from '../../supabase'
import { ecrire, lire } from './erreurs'
import {
  t, Stagger, Panel, PanelHead, PanelTitle, PageHead, PageTitle, PageSub,
  Btn, IconBtn, Input, Textarea, Label, Field, Row, Cluster, Pill, ChipBar, Chip,
  ListItem, ItemTitle, ItemMeta, Loading, Empty, Muted, useUI,
} from './ui'

const VIDE = {
  nom: '', km_approvisionnement: '', prix_paille: '', prix_foin: '',
  prix_luzerne: '', notes: '', actif: true, dispos: [],
}

/* ── Disponibilités en camions remorques ──
   `camions` à null veut dire « -- » : le fournisseur en a, sans qu'on
   sache combien. On décompte à la main à mesure qu'on réserve. */
const fmtCourt = (iso) => new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })

const camionsTexte = (n) => (n == null ? '--' : `${n} camion${n > 1 ? 's' : ''}`)

/* Total d'un produit sur plusieurs fournisseurs : les chiffres
   s'additionnent, un « -- » reste signalé à côté. */
function totalTexte(entrees) {
  const connus = entrees.filter((d) => d.camions != null)
  const somme = connus.reduce((a, d) => a + d.camions, 0)
  const inconnu = connus.length < entrees.length
  if (!connus.length) return '--'
  return inconnu ? `${somme} + --` : String(somme)
}

const Split = styled.div`
  display: grid;
  grid-template-columns: ${(p) => (p.$open ? '330px 1fr' : '1fr')};
  gap: 20px; align-items: start;
  @media (max-width: 1000px) { grid-template-columns: 1fr; }
`
const Col = styled.div`display: flex; flex-direction: column; gap: 16px;`
const Tarifs = styled.div`
  display: flex; gap: 12px; flex-wrap: wrap;
  font-family: ${t.font}; font-size: 0.72rem; color: ${t.textSoft};
  margin-top: 5px; font-variant-numeric: tabular-nums;
  b { color: ${t.text}; font-weight: 600; }
`
/* Carte fournisseur : l'en-tête ouvre la fiche, le bandeau du dessous
   se manipule sans l'ouvrir. */
const Carte = styled.div`
  margin-bottom: 7px;
  > button { margin-bottom: 0; }
  ${(p) => p.$dispos && `> button { border-bottom-left-radius: 0; border-bottom-right-radius: 0; }`}
`
const Dispos = styled.div`
  background: ${t.surface};
  border: 1px solid ${t.line}; border-top: none;
  border-radius: 0 0 ${t.r} ${t.r};
  padding: 4px 14px;
`
const BlocProduit = styled.div`
  padding: 5px 0;
  & + & { border-top: 1px solid ${t.lineSoft}; }
`
const LigneCamions = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  font-family: ${t.font}; font-size: 0.8rem; color: ${t.text};
`
/* Un camion réservé = une ligne = un client. */
const Reservation = styled.div`
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  margin: 5px 0 0 2px; padding: 5px 8px;
  border-left: 2px solid ${t.gold}; background: ${t.goldWash};
  border-radius: 0 ${t.rXs} ${t.rXs} 0;
  font-family: ${t.font}; font-size: 0.76rem; color: ${t.goldDeep};
  b { font-weight: 600; }
  em { font-style: normal; color: ${t.textFaint}; font-size: 0.7rem; font-variant-numeric: tabular-nums; }
  > div { margin-left: auto; display: flex; gap: 4px; }
`
const SaisieClient = styled.div`
  display: flex; gap: 6px; margin-top: 6px;
  input { flex: 1; min-width: 0; font-size: 16px; }
`
const Compteur = styled.div`
  display: flex; align-items: center; gap: 8px; flex-shrink: 0;
`
const Valeur = styled.span`
  min-width: 78px; text-align: center;
  font-family: ${t.font}; font-size: 0.8rem; font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${(p) => (p.$vide ? t.textFaint : p.$inconnu ? t.textSoft : t.gold)};
`
const Coche = styled.button`
  display: flex; align-items: center; gap: 8px; flex: 1; min-width: 0;
  padding: 8px 11px; border-radius: ${t.rSm}; cursor: pointer; text-align: left;
  font-family: ${t.font}; font-size: 0.8rem; font-weight: 540;
  background: ${(p) => (p.$on ? t.goldWash : t.surface)};
  border: 1px solid ${(p) => (p.$on ? t.gold : t.line)};
  color: ${(p) => (p.$on ? t.goldDeep : t.textSoft)};
  transition: background 0.14s, border-color 0.14s;
`
const SaisieDispo = styled.div`
  display: flex; align-items: center; gap: 8px; margin-bottom: 7px;
  input { width: 86px; text-align: center; font-size: 16px; } /* 16px : pas de zoom iOS */
  span { font-family: ${t.font}; font-size: 0.74rem; color: ${t.textFaint}; white-space: nowrap; }
`
/* Marchandise hors catalogue, ajoutée à la main en bas de la liste */
const AjoutLibre = styled.div`
  display: flex; align-items: center; gap: 8px; margin: 10px 0 7px;
  input { font-size: 16px; }
  input:first-child { flex: 1; min-width: 0; }
  input[type='number'] { width: 86px; flex-shrink: 0; text-align: center; }
  @media (max-width: 480px) { flex-wrap: wrap; input:first-child { flex-basis: 100%; } }
`

/* Une marchandise libre porte un identifiant tiré de son nom : la même
   saisie chez deux fournisseurs s'additionne dans le bilan. */
const idLibre = (nom) => `libre:${nom.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\s+/g, ' ').trim()}`

const Bilan = styled.div`
  margin-bottom: 16px;
`

const Bascule = styled.button`
  display: flex; align-items: center; gap: 8px;
  padding: 8px 13px; border-radius: ${t.rSm}; cursor: pointer;
  font-family: ${t.font}; font-size: 0.8rem; font-weight: 560;
  background: ${(p) => (p.$on ? t.greenWash : t.surfaceSunk)};
  border: 1px solid ${(p) => (p.$on ? '#CFE6D8' : t.line)};
  color: ${(p) => (p.$on ? t.green : t.textSoft)};
  transition: all 0.14s;
`

export default function FournisseursAdmin() {
  const { toast, confirm } = useUI()
  const [liste, setListe] = useState([])
  /* Copie à jour de la liste : deux taps rapides sur − doivent retirer
     deux camions, pas un seul calculé deux fois sur le même état. */
  const listeRef = useRef([])
  listeRef.current = liste
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(null) // objet | 'new' | null
  const [form, setForm] = useState(VIDE)
  const [saving, setSaving] = useState(false)
  const [produits, setProduits] = useState([])
  const [vue, setVue] = useState('all') // id produit affiché seul, ou 'all'
  const [libre, setLibre] = useState({ nom: '', camions: '' })
  const [reservant, setReservant] = useState(null) // { fid, pid } de la ligne en cours de réservation
  const [client, setClient] = useState('')
  const [clients, setClients] = useState([])

  useEffect(() => { charger() }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  const charger = async () => {
    setLoading(true)
    const data = await lire(
      supabase.from('fournisseurs').select('*').order('nom'),
      toast, 'chargement des fournisseurs',
    )
    if (data) setListe(data)
    const { data: p } = await supabase.from('products').select('id, name').order('name')
    if (p) setProduits(p)
    const { data: c } = await supabase.from('clients').select('name').order('name')
    if (c) setClients([...new Set(c.map((x) => x.name?.trim()).filter(Boolean))])
    setLoading(false)
  }

  /* Le nom courant du catalogue prime ; celui recopié sert si le produit
     a disparu depuis. */
  const nomProduit = (d) => produits.find((p) => p.id === d.produit_id)?.name?.trim() || d.produit

  /* −/+ depuis la liste : enregistré aussitôt, sans ouvrir la fiche. */
  /* Modifie une ligne de marchandise depuis la liste : enregistré aussitôt,
     sans ouvrir la fiche. */
  const modifierLigne = async (id, produitId, changer, quoi) => {
    const f = listeRef.current.find((x) => x.id === id)
    const dispos = (f.dispos || []).map((d) => (d.produit_id === produitId ? changer(d) : d))
    listeRef.current = listeRef.current.map((x) => (x.id === id ? { ...x, dispos } : x))
    setListe(listeRef.current)
    const ok = await ecrire(
      supabase.from('fournisseurs').update({ dispos, updated_at: new Date().toISOString() }).eq('id', id),
      toast, quoi,
    )
    if (!ok) charger()
    return ok
  }

  const ajuster = (id, produitId, delta) => modifierLigne(id, produitId, (d) => (
    d.camions == null ? d : { ...d, camions: Math.max(0, d.camions + delta) }
  ), 'mise à jour des camions')

  /* Réserver prend un camion sur le disponible et l'attribue à un client.
     Sur un « -- », le disponible reste inconnu : seul le nom s'ajoute. */
  const reserver = async () => {
    const nom = client.trim()
    if (!nom || !reservant) return
    const ok = await modifierLigne(reservant.fid, reservant.pid, (d) => ({
      ...d,
      camions: d.camions == null ? null : Math.max(0, d.camions - 1),
      reservations: [...(d.reservations || []),
        { id: `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`, client: nom, le: new Date().toISOString() }],
    }), 'réservation')
    if (ok) { setReservant(null); setClient('') }
  }

  /* Libérer : le client se désiste, le camion revient au disponible.
     Livré : le camion est parti, il ne revient nulle part. */
  const retirerReservation = (fid, pid, rid, rendre) => modifierLigne(fid, pid, (d) => ({
    ...d,
    camions: rendre && d.camions != null ? d.camions + 1 : d.camions,
    reservations: (d.reservations || []).filter((r) => r.id !== rid),
  }), rendre ? 'libération du camion' : 'retrait de la réservation')

  const ouvrir = (f) => {
    setSelected(f)
    setLibre({ nom: '', camions: '' })
    setForm({
      nom: f.nom || '',
      km_approvisionnement: f.km_approvisionnement ?? '',
      prix_paille: f.prix_paille ?? '',
      prix_foin: f.prix_foin ?? '',
      prix_luzerne: f.prix_luzerne ?? '',
      notes: f.notes || '',
      actif: f.actif !== false,
      dispos: (f.dispos || []).map((d) => ({ ...d, camions: d.camions ?? '' })),
    })
  }

  /* Coche un produit du catalogue dans la fiche : il part en « -- ». */
  const basculerProduit = (p) => setForm((f) => ({
    ...f,
    dispos: f.dispos.some((d) => d.produit_id === p.id)
      ? f.dispos.filter((d) => d.produit_id !== p.id)
      : [...f.dispos, { produit_id: p.id, produit: p.name.trim(), camions: '' }],
  }))
  const ajouterLibre = () => {
    const nom = libre.nom.trim().replace(/\s+/g, ' ')
    if (!nom) return
    /* Un nom du catalogue tapé à la main coche simplement le produit. */
    const duCatalogue = produits.find((p) => idLibre(p.name) === idLibre(nom))
    const id = duCatalogue?.id || idLibre(nom)
    if (form.dispos.some((d) => d.produit_id === id)) return toast(`${nom} est déjà dans la liste`, 'error')
    setForm((f) => ({
      ...f,
      dispos: [...f.dispos, { produit_id: id, produit: duCatalogue?.name.trim() || nom, camions: libre.camions }],
    }))
    setLibre({ nom: '', camions: '' })
  }

  const saisirCamions = (id, v) => setForm((f) => ({
    ...f, dispos: f.dispos.map((d) => (d.produit_id === id ? { ...d, camions: v } : d)),
  }))

  const nouveau = () => { setSelected('new'); setForm(VIDE); setLibre({ nom: '', camions: '' }) }

  const nombre = (v) => (v === '' || v == null ? null : parseFloat(v))

  const enregistrer = async () => {
    if (!form.nom.trim()) return toast('Le nom est requis', 'error')
    setSaving(true)
    const payload = {
      nom: form.nom.trim(),
      km_approvisionnement: nombre(form.km_approvisionnement),
      prix_paille: nombre(form.prix_paille),
      prix_foin: nombre(form.prix_foin),
      prix_luzerne: nombre(form.prix_luzerne),
      notes: form.notes || null,
      actif: form.actif,
      dispos: form.dispos.map((d) => ({
        produit_id: d.produit_id,
        produit: d.produit,
        camions: d.camions === '' || d.camions == null ? null : Math.max(0, parseInt(d.camions, 10) || 0),
        /* Les réservations se font depuis la liste, fiche ouverte ou non :
           on reprend les plus récentes plutôt que celles de l'ouverture. */
        reservations: (selected !== 'new' && listeRef.current.find((x) => x.id === selected.id)?.dispos
          ?.find((x) => x.produit_id === d.produit_id)?.reservations) || d.reservations || [],
      })),
      updated_at: new Date().toISOString(),
    }
    const req = selected === 'new'
      ? supabase.from('fournisseurs').insert([payload])
      : supabase.from('fournisseurs').update(payload).eq('id', selected.id)
    const ok = await ecrire(req, toast, 'enregistrement du fournisseur')
    setSaving(false)
    if (!ok) return
    toast(selected === 'new' ? 'Fournisseur créé' : 'Fournisseur mis à jour')
    setSelected(null)
    charger()
  }

  const supprimer = async () => {
    const ok = await confirm({
      icon: '🗑️', tone: 'red',
      title: 'Supprimer ce fournisseur ?',
      body: <><strong>{selected.nom}</strong> disparaîtra de la liste. Les devis déjà chiffrés
        gardent le fournisseur et les prix qui leur ont été appliqués.</>,
      confirmLabel: 'Supprimer',
    })
    if (!ok) return
    await supabase.from('fournisseurs').delete().eq('id', selected.id)
    toast('Fournisseur supprimé')
    setSelected(null)
    charger()
  }

  /* Le point par produit, sur les fournisseurs actifs : ce qui reste à
     réserver, tous fournisseurs confondus. */
  const actifs = liste.filter((f) => f.actif !== false)
  const bilan = [...new Set(actifs.flatMap((f) => (f.dispos || []).map((d) => d.produit_id)))]
    .map((id) => {
      const entrees = actifs.flatMap((f) => (f.dispos || []).filter((d) => d.produit_id === id))
      const reserves = entrees.reduce((a, d) => a + (d.reservations?.length || 0), 0)
      return { id, nom: nomProduit(entrees[0]), total: totalTexte(entrees), reserves }
    })
    .sort((a, b) => a.nom.localeCompare(b.nom, 'fr'))

  const visibles = vue === 'all' ? liste : liste.filter((f) => (f.dispos || []).some((d) => d.produit_id === vue))

  /* Marchandises hors catalogue — ajoutées à la main, ou retirées du
     catalogue depuis : on les garde visibles pour pouvoir les décocher. */
  const choixProduits = [
    ...produits.map((p) => ({ id: p.id, name: p.name.trim() })),
    ...form.dispos.filter((d) => !produits.some((p) => p.id === d.produit_id))
      .map((d) => ({ id: d.produit_id, name: d.produit })),
  ]

  return (
    <>
      <PageHead>
        <div>
          <PageTitle>Fournisseurs</PageTitle>
          <PageSub>
            {liste.length} fournisseur(s) · les tarifs servent à pré-remplir un calcul,
            ils ne modifient jamais un devis déjà chiffré
          </PageSub>
        </div>
        <Btn $variant="gold" onClick={nouveau}>+ Nouveau fournisseur</Btn>
      </PageHead>

      {/* Suggestions tirées du carnet clients ; un nom libre reste accepté. */}
      <datalist id="clients-fournisseurs">
        {clients.map((n) => <option key={n} value={n} />)}
      </datalist>

      <Split $open={!!selected}>
        <Col>
          {loading ? (
            <Loading rows={3} h="74px" />
          ) : liste.length === 0 ? (
            <Empty icon="🚜" title="Aucun fournisseur">
              Enregistrez vos fournisseurs avec leur kilométrage et leurs tarifs :
              le coût de revient d'un devis se calculera ensuite en un clic.
            </Empty>
          ) : (
            <>
              {bilan.length > 0 && (
                <Bilan>
                  <Label>Camions remorques disponibles</Label>
                  <ChipBar $scroll>
                    <Chip $on={vue === 'all'} aria-pressed={vue === 'all'} onClick={() => setVue('all')}>
                      Tous les fournisseurs
                    </Chip>
                    {bilan.map((b) => (
                      <Chip key={b.id} $on={vue === b.id} aria-pressed={vue === b.id}
                        onClick={() => setVue(vue === b.id ? 'all' : b.id)}>
                        {b.nom} <span>{b.total}{b.reserves > 0 && ` · 🔖 ${b.reserves}`}</span>
                      </Chip>
                    ))}
                  </ChipBar>
                </Bilan>
              )}
              {visibles.map((f, i) => {
                const dispos = (f.dispos || []).filter((d) => vue === 'all' || d.produit_id === vue)
                return (
                  <Stagger key={f.id} $i={i}>
                    <Carte $dispos={dispos.length > 0}>
                      <ListItem $on={selected?.id === f.id} onClick={() => ouvrir(f)}>
                        <Cluster $gap="8px">
                          <ItemTitle>{f.nom}</ItemTitle>
                          {f.actif === false && <Pill $tone="neutral">inactif</Pill>}
                        </Cluster>
                        <ItemMeta>
                          {f.km_approvisionnement != null ? `${f.km_approvisionnement} km` : 'km non renseigné'}
                        </ItemMeta>
                        <Tarifs>
                          <span>paille <b>{f.prix_paille != null ? `${f.prix_paille} €` : '—'}</b></span>
                          <span>foin <b>{f.prix_foin != null ? `${f.prix_foin} €` : '—'}</b></span>
                          <span>luzerne <b>{f.prix_luzerne != null ? `${f.prix_luzerne} €` : '—'}</b></span>
                        </Tarifs>
                      </ListItem>
                      {dispos.length > 0 && (
                        <Dispos>
                          {dispos.map((d) => (
                            <BlocProduit key={d.produit_id}>
                              <LigneCamions>
                                <span>{nomProduit(d)}</span>
                                <Compteur>
                                  <IconBtn type="button" title="Réserver un camion pour un client"
                                    aria-label={`Réserver un camion de ${nomProduit(d)} pour un client`}
                                    disabled={d.camions === 0}
                                    onClick={() => { setReservant({ fid: f.id, pid: d.produit_id }); setClient('') }}>🔖</IconBtn>
                                  {d.camions != null && (
                                    <IconBtn type="button" aria-label={`Retirer un camion de ${nomProduit(d)}`}
                                      disabled={d.camions === 0} onClick={() => ajuster(f.id, d.produit_id, -1)}>−</IconBtn>
                                  )}
                                  <Valeur $inconnu={d.camions == null} $vide={d.camions === 0}
                                    title={d.camions == null ? 'Disponible, quantité inconnue' : undefined}>
                                    {d.camions === 0 ? '0 · épuisé' : camionsTexte(d.camions)}
                                  </Valeur>
                                  {d.camions != null && (
                                    <IconBtn type="button" aria-label={`Ajouter un camion de ${nomProduit(d)}`}
                                      onClick={() => ajuster(f.id, d.produit_id, 1)}>+</IconBtn>
                                  )}
                                </Compteur>
                              </LigneCamions>
                              {(d.reservations || []).map((r) => (
                                <Reservation key={r.id}>
                                  <span>🔖 <b>{r.client}</b> <em>· {fmtCourt(r.le)}</em></span>
                                  <div>
                                    <Btn type="button" $variant="quiet" $size="sm" title="Le camion revient au disponible"
                                      onClick={() => retirerReservation(f.id, d.produit_id, r.id, true)}>Libérer</Btn>
                                    <Btn type="button" $variant="quiet" $size="sm" title="Le camion est parti : on retire la réservation"
                                      onClick={() => retirerReservation(f.id, d.produit_id, r.id, false)}>Livré ✓</Btn>
                                  </div>
                                </Reservation>
                              ))}
                              {reservant?.fid === f.id && reservant?.pid === d.produit_id && (
                                <SaisieClient>
                                  <Input autoFocus list="clients-fournisseurs" value={client}
                                    placeholder="Nom du client" aria-label="Nom du client"
                                    onChange={(e) => setClient(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') reserver()
                                      if (e.key === 'Escape') setReservant(null)
                                    }} />
                                  <Btn type="button" $size="sm" onClick={reserver} disabled={!client.trim()}>Réserver</Btn>
                                  <Btn type="button" $variant="quiet" $size="sm" onClick={() => setReservant(null)}>✕</Btn>
                                </SaisieClient>
                              )}
                            </BlocProduit>
                          ))}
                        </Dispos>
                      )}
                    </Carte>
                  </Stagger>
                )
              })}
              {visibles.length === 0 && (
                <Empty icon="🚛" title="Aucun fournisseur pour ce produit">
                  <Chip onClick={() => setVue('all')}>Voir tous les fournisseurs</Chip>
                </Empty>
              )}
            </>
          )}
        </Col>

        {selected && (
          <Col>
            <Stagger>
              <Panel $accent>
                <PanelHead>
                  <PanelTitle>{selected === 'new' ? 'Nouveau fournisseur' : form.nom || 'Fournisseur'}</PanelTitle>
                  <Btn $variant="quiet" $size="sm" onClick={() => setSelected(null)}>✕ Fermer</Btn>
                </PanelHead>

                <Row $cols="1fr 160px">
                  <Field>
                    <Label>Nom *</Label>
                    <Input value={form.nom} onChange={(e) => setForm((p) => ({ ...p, nom: e.target.value }))}
                      placeholder="ex : Ferme Dubois, Coopérative de …" />
                  </Field>
                  <Field>
                    <Label>Kilométrage</Label>
                    <Input type="number" value={form.km_approvisionnement}
                      onChange={(e) => setForm((p) => ({ ...p, km_approvisionnement: e.target.value }))}
                      placeholder="180" />
                  </Field>
                </Row>

                <Label>Tarifs à la tonne (€)</Label>
                <Row $cols="1fr 1fr 1fr">
                  <Field>
                    <Label>Paille</Label>
                    <Input type="number" step="0.01" value={form.prix_paille}
                      onChange={(e) => setForm((p) => ({ ...p, prix_paille: e.target.value }))} placeholder="100" />
                  </Field>
                  <Field>
                    <Label>Foin</Label>
                    <Input type="number" step="0.01" value={form.prix_foin}
                      onChange={(e) => setForm((p) => ({ ...p, prix_foin: e.target.value }))} placeholder="—" />
                  </Field>
                  <Field>
                    <Label>Luzerne</Label>
                    <Input type="number" step="0.01" value={form.prix_luzerne}
                      onChange={(e) => setForm((p) => ({ ...p, prix_luzerne: e.target.value }))} placeholder="—" />
                  </Field>
                </Row>

                <Field>
                  <Label>Disponible en camions remorques</Label>
                  {choixProduits.map((p) => {
                    const d = form.dispos.find((x) => x.produit_id === p.id)
                    return (
                      <SaisieDispo key={p.id}>
                        <Coche type="button" $on={!!d} aria-pressed={!!d} onClick={() => basculerProduit(p)}>
                          {d ? '✓' : '+'} {p.name}
                          {d?.reservations?.length > 0 && <Pill $tone="gold">🔖 {d.reservations.length} réservé{d.reservations.length > 1 ? 's' : ''}</Pill>}
                        </Coche>
                        {d && (
                          <>
                            <Input type="number" inputMode="numeric" min="0" step="1" placeholder="--"
                              aria-label={`Camions de ${p.name}`}
                              value={d.camions} onChange={(e) => saisirCamions(p.id, e.target.value)} />
                            <span>camions</span>
                          </>
                        )}
                      </SaisieDispo>
                    )
                  })}
                  <AjoutLibre>
                    <Input value={libre.nom} placeholder="Autre marchandise…" aria-label="Nom de la marchandise"
                      onChange={(e) => setLibre((l) => ({ ...l, nom: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); ajouterLibre() } }} />
                    <Input type="number" inputMode="numeric" min="0" step="1" placeholder="--"
                      aria-label="Camions de cette marchandise" value={libre.camions}
                      onChange={(e) => setLibre((l) => ({ ...l, camions: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); ajouterLibre() } }} />
                    <Btn type="button" $variant="ghost" onClick={ajouterLibre} disabled={!libre.nom.trim()}>Ajouter</Btn>
                  </AjoutLibre>
                  <Muted style={{ fontSize: '0.72rem', marginTop: 4 }}>
                    Laisser la case vide pour « -- » : il en a, sans savoir combien.
                    Ensuite, les − / + de la liste décomptent à mesure que vous réservez.
                  </Muted>
                </Field>

                <Field>
                  <Label>Notes</Label>
                  <Textarea value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                    placeholder="Qualité, conditions, contact…" style={{ minHeight: 72 }} />
                </Field>

                <Field>
                  <Label>Disponibilité</Label>
                  <Bascule $on={form.actif} onClick={() => setForm((p) => ({ ...p, actif: !p.actif }))}>
                    {form.actif ? '✅ Proposé dans les calculs' : '🚫 Masqué des calculs'}
                  </Bascule>
                </Field>

                <Muted style={{ fontSize: '0.74rem', marginBottom: 14 }}>
                  Ces tarifs sont ceux du moment. Ils pré-remplissent un calcul de coût, et
                  restent modifiables devis par devis selon la qualité ou la saison.
                </Muted>

                <Cluster $end $gap="9px">
                  {selected !== 'new' && <Btn $variant="danger" onClick={supprimer}>Supprimer</Btn>}
                  <Btn $variant="gold" onClick={enregistrer} disabled={saving}>
                    {saving ? 'Enregistrement…' : 'Enregistrer'}
                  </Btn>
                </Cluster>
              </Panel>
            </Stagger>
          </Col>
        )}
      </Split>
    </>
  )
}
